import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ListChecks, Gift, Users, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Avatar } from './Avatar';

export const ParentMenu: React.FC = () => {
    const { session, signOut } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
    const avatarMenuRef = useRef<HTMLDivElement | null>(null);

    const handleSignOut = async () => {
        await signOut();
        navigate('/');
    };

    const navLinks = [
        { to: '/parent/dashboard', icon: LayoutDashboard, label: t.nav.dashboard },
        { to: '/parent/quests', icon: ListChecks, label: t.nav.quests },
        { to: '/parent/rewards', icon: Gift, label: t.nav.rewards },
        { to: '/parent/family', icon: Users, label: t.nav.family },
    ];

    const isActive = (path: string) => location.pathname === path;

    useEffect(() => {
        if (!isAvatarMenuOpen) return;

        const onPointerDown = (e: PointerEvent) => {
            const el = avatarMenuRef.current;
            if (!el) return;
            if (e.target instanceof Node && !el.contains(e.target)) {
                setIsAvatarMenuOpen(false);
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsAvatarMenuOpen(false);
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isAvatarMenuOpen]);

    return (
        <nav className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-20">
            <div className="max-w-6xl mx-auto relative flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-4 md:gap-6">
                    {/* Mobile Menu Toggle */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden p-1 -ml-1 text-slate-500 hover:text-purple-600 outline-none"
                    >
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>

                    <div className="hidden md:flex items-center gap-6">
                        <Link to="/parent/dashboard" className="flex items-center">
                            <img
                                src="/NestQ_logo_2026.png"
                                alt="NestQ"
                                className="h-8 rounded-lg"
                            />
                        </Link>

                        {/* Desktop Nav */}
                        {navLinks.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`flex items-center gap-2 transition-colors ${isActive(link.to) ? 'text-purple-600 font-semibold' : 'text-slate-600 hover:text-purple-600'}`}
                            >
                                <link.icon size={18} />
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Centered Logo (Mobile) */}
                <div className="md:hidden absolute left-1/2 -translate-x-1/2">
                    <div className="flex items-center justify-center">
                        <Link to="/parent/dashboard" onClick={() => setIsMenuOpen(false)} className="flex items-center">
                            <img
                                src="/NestQ_logo_2026.png"
                                alt="NestQ"
                                className="h-8 rounded-lg"
                            />
                        </Link>
                    </div>
                </div>

                <div className="relative flex items-center justify-end gap-2 md:gap-4 flex-shrink-0" ref={avatarMenuRef}>
                    <button
                        type="button"
                        onClick={() => setIsAvatarMenuOpen((v) => !v)}
                        className="flex h-10 items-center gap-2 rounded-lg px-2 hover:bg-slate-50 transition-colors outline-none"
                        aria-haspopup="menu"
                        aria-expanded={isAvatarMenuOpen}
                    >
                        <span className="max-w-28 sm:max-w-40 truncate text-sm font-medium text-slate-700">{session.user?.display_name}</span>
                        <Avatar name={session.user?.display_name || ''} size="sm" variant="parent" />
                    </button>

                    {isAvatarMenuOpen && (
                        <div
                            role="menu"
                            className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
                        >
                            <button
                                type="button"
                                role="menuitem"
                                onClick={async () => {
                                    setIsAvatarMenuOpen(false);
                                    await handleSignOut();
                                }}
                                className="w-full px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                            >
                                <LogOut size={18} className="text-red-500" />
                                {t.common.sign_out}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Nav Dropdown */}
            {isMenuOpen && (
                <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-xl animate-in slide-in-from-top duration-200">
                    <div className="flex flex-col p-4 gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive(link.to) ? 'bg-purple-50 text-purple-600 font-bold' : 'text-slate-600 active:bg-slate-50'}`}
                            >
                                <link.icon size={20} />
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
};
